'use client';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOpenShifts } from 'features/shift/shiftApi';
import { bookShift } from 'features/booking/bookingApi';

import Button from 'components/base/Button';
import ConfirmBookingModal from 'components/modals/ConfirmBookingModal';
import SuccessModal from 'components/modals/SuccessModal';
import { LocationAutocomplete } from 'components/common/LocationAutocomplete';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faClock,
  faUsers,
  faUserMd,
  faPoundSign,
  faSearch,
  faList,
  faChevronDown,
  faHospital,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { getProfile, LocumProfile } from 'features/profile/profileApi';

const LocumFindShiftsPage = () => {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [bookingId, setBookingId] = useState<number | null>(null);
  const [selectedShift, setSelectedShift] = useState<any | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // 🔹 NEW: Add travel distance state
  const [travelDistance, setTravelDistance] = useState<number | null>(null);

  const [filters, setFilters] = useState({
    location: '',
    latitude: null,
    longitude: null,
    date_from: '',
    date_to: '',
    min_pay_rate: '',
    max_pay_rate: '',
    locum_role: ''
  });

  // FETCH SHIFTS
  const fetchShifts = async (pageNumber = 1) => {
    try {
      setLoading(true);

      const data = await getOpenShifts({
        ...filters,
        travel_distance: travelDistance, 
        page: pageNumber,
        limit: 6
      });

      setShifts(data.shifts || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setTotalPages(data.totalPages || 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  const loadLocumLocation = async () => {
    try {
      setLoading(true);
      
      // Fetch locum profile
      const profile = await getProfile();
      console.log('Fetched locum profile:', profile);
      
      // Type guard: ensure it's a locum profile
      if (profile && 'profile' in profile && profile.profile.location) {
        const locumProfile = profile as LocumProfile;
        
        // Set location and coordinates in filters
        setFilters(prev => ({
          ...prev,
          location: locumProfile.profile.location,
          locum_role: locumProfile.profile.locum_role
        }));

        // 🔹 NEW: Store travel distance to display
        if (locumProfile.profile.travel_distance) {
          setTravelDistance(locumProfile.profile.travel_distance);
        }
      }
      
      // Fetch shifts (with or without location)
      fetchShifts(1);
    } catch (err) {
      console.error('Failed to load locum profile:', err);
      // Fallback: just fetch shifts without default location
      fetchShifts(1);
    }
  };

  loadLocumLocation();
}, []);
  // BOOK SHIFT
  const handleBook = (shift: any) => {
    setSelectedShift(shift);
    setShowConfirmModal(true);
  };

  const handleConfirmBook = async () => {
    if (!selectedShift) return;

    try {
      setBookingId(selectedShift.id);

      await bookShift(selectedShift.id);

      // update UI instantly
      setShifts(prev =>
        prev.map(s => (s.id === selectedShift.id ? { ...s, is_booked: true } : s))
      );
      setShowConfirmModal(false);
      setSuccessMessage(`Your shift has been booked successfully. Check your bookings for details.`);
      setShowSuccessModal(true);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setBookingId(null);
    }
  };

  const handleSearch = () => {
    fetchShifts(1);
  };

  return (
    <div className="container py-2">
      <h2 className="fw-bold mb-3">Find Shifts</h2>

      {/* TOTAL + TRAVEL DISTANCE LABEL */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted">
          <FontAwesomeIcon icon={faList} className="me-2" />
          {total} shifts found
        </span>

        {/* 🔹 NEW: Display travel distance */}
        {travelDistance && (
          <span className="badge bg-info text-white">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
            Searching within {travelDistance} Miles
          </span>
        )}
      </div>

      {/* FILTER BAR */}
      <div className="card shadow-sm border-0 p-4 mb-4 mt-4">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr',
            gap: '1rem',
            alignItems: 'flex-end'
          }}
        >
          {/* LOCATION */}
          <div>
            <label className="form-label fw-semibold text-body-highlight mb-2 d-block">
              Location
            </label>
            <div style={{ minHeight: '38px' }}>
              <LocationAutocomplete
                value={filters.location}
                onChange={(location: string) =>
                  setFilters(prev => ({ ...prev, location }))
                }
                onCoordinatesChange={(coords: any) =>
                  setFilters(prev => ({
                    ...prev,
                    latitude: coords.lat,
                    longitude: coords.lng
                  }))
                }
                placeholder="Search location"
              />
            </div>
          </div>

          {/* START DATE */}
          <div>
            <label className="form-label fw-semibold text-body-highlight mb-2 d-block">
              Start Date
            </label>
            <input
              type="date"
              className="form-control"
              value={filters.date_from}
              onChange={e => setFilters({ ...filters, date_from: e.target.value })}
            />
          </div>

          {/* END DATE */}
          <div>
            <label className="form-label fw-semibold text-body-highlight mb-2 d-block">
              End Date
            </label>
            <input
              type="date"
              className="form-control"
              value={filters.date_to}
              onChange={e => setFilters({ ...filters, date_to: e.target.value })}
            />
          </div>

          {/* MORE FILTERS BUTTON */}
          <div>
            <Button
              variant="secondary"
              className="w-100"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              style={{ whiteSpace: 'nowrap' }}
            >
              More Filters
              <FontAwesomeIcon
                icon={faChevronDown}
                className="ms-2"
                style={{
                  transform: showAdvancedFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              />
            </Button>
          </div>

          {/* SEARCH BUTTON */}
          <div>
            <Button
              variant="phoenix-primary"
              className="w-100"
              onClick={handleSearch}
              startIcon={<FontAwesomeIcon icon={faSearch} />}
            >
              Search
            </Button>
          </div>
        </div>

        {/* ADVANCED FILTERS - COLLAPSIBLE */}
        {showAdvancedFilters && (
          <div
            className="row g-3 mt-2 pt-3"
            style={{
              animation: 'fadeIn 0.3s ease'
            }}
          >
            {/* MIN PAY */}
            <div className="col-md-4">
              <label className="form-label fw-semibold text-body-highlight mb-2">
                Minimum Pay (£)
              </label>
              <input
                type="number"
                placeholder="Enter minimum pay"
                className="form-control"
                value={filters.min_pay_rate}
                onChange={e =>
                  setFilters({ ...filters, min_pay_rate: e.target.value })
                }
              />
            </div>

            {/* MAX PAY */}
            <div className="col-md-4">
              <label className="form-label fw-semibold text-body-highlight mb-2">
                Maximum Pay (£)
              </label>
              <input
                type="number"
                placeholder="Enter maximum pay"
                className="form-control"
                value={filters.max_pay_rate}
                onChange={e =>
                  setFilters({ ...filters, max_pay_rate: e.target.value })
                }
              />
            </div>

            {/* LOCUM ROLE */}
            <div className="col-md-4">
              <label className="form-label fw-semibold text-body-highlight mb-2">
                Position Type
              </label>
              <select
                className="form-control"
                value={filters.locum_role}
                onChange={e =>
                  setFilters({ ...filters, locum_role: e.target.value })
                }
              >
                <option value="all">All Positions</option>
                <option value="pharmacist">Pharmacist</option>
                <option value="technician">Technician</option>
                <option value="dispenser">Dispenser</option>
              </select>
            </div>
          </div>
        )}

        <style>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              max-height: 0;
            }
            to {
              opacity: 1;
              max-height: 500px;
            }
          }
        `}</style>
      </div>

      {/* STATES */}
      {loading && <p className="text-muted">Loading shifts...</p>}
      {error && <p className="text-danger">{error}</p>}

      {!loading && shifts.length === 0 && (
        <div className="text-center text-muted mt-5">
          <h5>No shifts available</h5>
        </div>
      )}

      {/* SHIFT LIST */}
      <div className="row g-4">
        {shifts.map(shift => (
          <div className="col-md-4" key={shift.id}>
            <div className="card border-0 shadow-sm h-100 p-3">
              {/* PHARMACY NAME */}
              <div className="mb-2">
                <small className="text-muted d-block">
                  <FontAwesomeIcon icon={faHospital} className="me-1" />
                  <strong>{shift.pharmacy_name || 'Pharmacy'}</strong>
                </small>
              </div>

              {/* LOCATION + SLOTS BADGE */}
              <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                <div className="flex-grow-1">
                  <h5 className="fw-bold mb-1">{shift.location}</h5>
                </div>
                <span className="badge bg-success text-nowrap">
                  {shift.available_slots} Slots
                </span>
              </div>

              {/* ROLE BADGE */}
              <div className="mb-2">
                <span className="badge bg-primary text-capitalize">
                  <FontAwesomeIcon icon={faUserMd} className="me-1" />
                  {shift.locum_role}
                </span>
              </div>

              {/* DATE */}
              <p className="text-muted mb-2 small">
                <FontAwesomeIcon icon={faCalendar} className="me-2" />
                {new Date(shift.date).toLocaleDateString('en-GB', {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })}
              </p>

              {/* TIME + BREAK */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <p className="mb-0">
                  <FontAwesomeIcon icon={faClock} className="me-2 text-primary" />
                  {shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}
                </p>
                {shift.lunch_break_minutes !== null && shift.lunch_break_minutes !== undefined && (
                  <small className="text-muted">
                    {shift.lunch_break_minutes === 0 ? 'No break' : `Break: ${shift.lunch_break_minutes} mins`}
                  </small>
                )}
              </div>

              {/* PAY + LOCUMS BOOKED */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold">
                  <FontAwesomeIcon
                    icon={faPoundSign}
                    className="me-2 text-success"
                  />
                  {shift.pay_rate}
                </span>

                <span className="text-muted small">
                  <FontAwesomeIcon icon={faUsers} className="me-1" />
                  {shift.booked_locums_count}/{shift.required_locums}
                </span>
              </div>

              <hr />

              {/* BUTTONS */}
              <div className="d-flex gap-2">
                <Button
                  variant="secondary"
                  className="flex-grow-1"
                  onClick={() => navigate(`/dashboard/shift/${shift.id}`)}
                >
                  View Details
                </Button>

                {shift.is_booked ? (
                  <Button variant="secondary" className="flex-grow-1" disabled>
                    ✔ Booked
                  </Button>
                ) : shift.available_slots === 0 ? (
                  <Button variant="secondary" className="flex-grow-1" disabled>
                    Full
                  </Button>
                ) : (
                  <Button
                    variant="phoenix-primary"
                    className="flex-grow-1"
                    onClick={() => handleBook(shift)}
                    disabled={bookingId === shift.id}
                  >
                    {bookingId === shift.id ? 'Booking...' : 'Book Shift'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION */}
      <ul className="pagination justify-content-center mt-4">
        {[...Array(totalPages)].map((_, i) => (
          <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
            <button className="page-link" onClick={() => fetchShifts(i + 1)}>
              {i + 1}
            </button>
          </li>
        ))}
      </ul>

      {/* 🔹 Confirmation Modal */}
      <ConfirmBookingModal
        show={showConfirmModal}
        onConfirm={handleConfirmBook}
        onCancel={() => setShowConfirmModal(false)}
        isLoading={bookingId !== null}
      />

      {/* 🔹 Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        title="Booking Confirmed!"
        message={successMessage || 'Shift booked successfully!'}
        onClose={() => setShowSuccessModal(false)}
        closeLabel="Close"
        closeDelay={2000}
      />
    </div>
  );
};

export default LocumFindShiftsPage;
